<template>
  <div class="video-play-page">
    <!-- Header 导航栏 -->
    <CommonHeader />

    <!-- Main 内容区 -->
    <main class="main-content">
      <div class="container">
                <!-- 面包屑导航 -->
        <div class="breadcrumb">
          <router-link
            v-if="fromPublic"
            to="/publicVideo"
            class="breadcrumb-link">公区视频库</router-link>
          <router-link
            v-else-if="fromDepartment"
            to="/departmentVideo"
            class="breadcrumb-link">部门视频库</router-link>
          <router-link
            v-else-if="fromTraining"
            :to="getTrainingBackRoute()"
            class="breadcrumb-link">培训资料</router-link>
          <router-link
            v-else
            to="/activityVideo"
            class="breadcrumb-link">集中活动</router-link>
          <i class="el-icon-arrow-right breadcrumb-separator"></i>
          <span class="breadcrumb-current">{{ getBreadcrumbTitle() }}</span>
        </div>
        <div class="content-wrapper">
          <!-- 左侧视频区域 -->
          <div class="video-section">
            <el-alert
              v-if="shouldShowNetworkAlert"
              class="video-network-alert"
              :title="networkAlertTitle"
              type="info"
              :closable="false"
              show-icon
            />
            <!-- 视频播放器 -->
            <div
              ref="videoWrapper"
              class="video-player-wrapper"
              :class="{
                'is-mobile': isMobilePage,
                'is-mobile-fullscreen': isMobileCssFullscreen
              }"
              @click="handlePlayerAreaClick"
            >
              <video
                ref="videoPlayer"
                class="video-player video-js"
                :crossorigin="videoCrossOriginAttr"
                preload="auto"
                playsinline
                webkit-playsinline="true"
                x5-playsinline="true"
                x5-video-player-type="h5"
                @play="handlePlay"
                @pause="handlePause"
                @timeupdate="handleTimeUpdate"
                @progress="handleProgress"
                @loadedmetadata="handleLoadedMetadata"
                @loadstart="handleLoadStart"
                @loadeddata="handleLoadedData"
                @canplay="handleCanPlay"
                @playing="handlePlaying"
                @waiting="handleWaiting"
                @stalled="handleStalled"
                @error="handleVideoError"
                @webkitbeginfullscreen="handleWebkitBeginFullscreen"
                @webkitendfullscreen="handleWebkitEndFullscreen"
              ></video>

              <div v-if="showPosterLayer" class="video-poster-layer">
                <img
                  :src="videoPosterUrl"
                  alt="视频封面"
                  class="video-poster-image"
                  @load="handlePosterLoad"
                  @error="handlePosterError"
                />
              </div>

              <div v-if="isVideoLoading" class="video-status-overlay">
                <i class="el-icon-loading video-status-icon"></i>
                <span>{{ videoLoadingText }}</span>
              </div>

              <div v-else-if="videoLoadError" class="video-status-overlay is-error">
                <p class="video-status-text">{{ videoLoadError }}</p>
                <button type="button" class="video-retry-btn" @click.stop="retryVideoLoad">
                  重新加载
                </button>
              </div>

              <transition name="continue-play-tip-fade">
                <div
                  v-if="continuePlayTipVisible"
                  class="continue-play-tip"
                  :class="{ 'is-mobile': isMobilePage }"
                  @click.stop
                >
                  <span class="continue-play-tip-text">{{ continuePlayTipText }}</span>
                  <button type="button" class="continue-play-tip-action" @click.stop="restartPlaybackFromBeginning">
                    从头播放
                  </button>
                </div>
              </transition>

              <button
                v-if="videoUrl && !isVideoLoading && !videoLoadError"
                type="button"
                class="center-play-toggle-zone"
                :class="{ 'is-playing': isPlaying }"
                :aria-label="isMobilePage && isPlaying ? '显示播放控件' : (isPlaying ? '暂停视频' : '播放视频')"
                @click.stop="togglePlayFromCenter"
              ></button>

              <div
                v-if="showMobileSeekZones"
                class="mobile-seek-gesture-layer"
              >
                <button
                  type="button"
                  class="mobile-seek-zone is-left"
                  :class="{ 'is-active': mobileSeekActiveDirection === 'backward' }"
                  aria-label="长按左侧快退"
                  @click.stop.prevent="handleMobileGestureZoneTap"
                  @touchstart.stop.prevent="handleMobileSeekTouchStart('backward', $event)"
                  @touchend.stop.prevent="handleMobileSeekTouchEnd"
                  @touchcancel.stop.prevent="handleMobileSeekTouchCancel"
                >
                </button>
                <button
                  type="button"
                  class="mobile-seek-zone is-right"
                  :class="{ 'is-active': mobileSeekActiveDirection === 'forward' }"
                  aria-label="长按右侧快进"
                  @click.stop.prevent="handleMobileGestureZoneTap"
                  @touchstart.stop.prevent="handleMobileSeekTouchStart('forward', $event)"
                  @touchend.stop.prevent="handleMobileSeekTouchEnd"
                  @touchcancel.stop.prevent="handleMobileSeekTouchCancel"
                >
                </button>
              </div>

              <div
                v-if="mobileSeekIndicatorText"
                class="mobile-seek-indicator"
                :class="{
                  'is-backward': mobileSeekActiveDirection === 'backward',
                  'is-forward': mobileSeekActiveDirection === 'forward'
                }"
              >
                {{ mobileSeekIndicatorText }}
              </div>

              <!-- 播放按钮遮罩 -->
              <div class="video-overlay" v-show="!isPlaying && !isVideoLoading && !videoLoadError" @click.stop="togglePlay">
                <div class="play-button">
                  <i class="el-icon-video-play"></i>
                </div>
              </div>

              <div ref="videoControls" class="video-controls" :style="controlsPanelStyle" @click.stop="handleControlInteraction">
                <!-- 进度条 -->
                <div
                  ref="progressBarWrapper"
                  class="progress-bar-wrapper"
                  :style="progressBarStyle"
                  @mousedown.stop.prevent="handleProgressMouseDown"
                  @touchstart.stop.prevent="handleProgressTouchStart"
                >
                  <div class="progress-bar">
                    <div class="progress-buffered" :style="{ width: bufferedPercent + '%' }"></div>
                    <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
                  </div>
                </div>

                <!-- 控制按钮 -->
                <div class="controls-bottom" :style="controlsBottomStyle">
                  <div class="controls-left" :style="controlsGroupStyle">
                    <button class="control-btn" :style="controlBtnStyle" @click="togglePlay">
                      <i :class="isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'" :style="controlIconStyle"></i>
                    </button>
                    <div class="volume-control">
                      <button class="control-btn volume-btn" :style="controlBtnStyle" @click="toggleMute">
                        <i :class="isMuted ? 'el-icon-bell' : 'el-icon-bell'" :style="controlIconStyle"></i>
                      </button>
                      <div v-if="!isMobilePage" class="volume-slider" :style="volumeSliderStyle">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          v-model="volume"
                          @input="changeVolume"
                          class="volume-input"
                        />
                      </div>
                    </div>
                    <span class="time-display" :style="timeDisplayStyle">{{ currentTimeFormatted }} / {{ durationFormatted }}</span>
                  </div>

                  <div class="controls-right" :style="controlsGroupStyle">
                    <div class="playback-rate" :style="playbackRateStyle">
                      <select
                        v-if="isMobilePage"
                        class="rate-select"
                        :value="playbackRate"
                        @change="changePlaybackRate(Number($event.target.value))"
                      >
                        <option v-for="rate in playbackRates" :key="rate" :value="rate">{{ rate }}x</option>
                      </select>
                      <button
                        v-else
                        v-for="rate in playbackRates"
                        :key="rate"
                        :class="['rate-btn', { active: playbackRate === rate }]"
                        :style="getRateBtnStyle(rate)"
                        @click="changePlaybackRate(rate)"
                      >
                        {{ rate }}x
                      </button>
                    </div>
                    <button v-if="!isMobilePage" class="control-btn" :style="controlBtnStyle" @click="captureScreenshot" title="截图">
                      <i class="el-icon-picture" :style="controlIconStyle"></i>
                    </button>
                    <button class="control-btn" :style="controlBtnStyle" @click="toggleFullscreen">
                      <i class="el-icon-full-screen" :style="controlIconStyle"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 视频信息 -->
            <div class="video-info-section">
              <h1 class="video-title">{{ videoDetail.videoTitle }}</h1>

              <div class="video-meta">
                <div class="video-meta-left">
                  <div class="meta-author">
                    <div class="author-avatar" :style="{ backgroundImage: `url(${getAuthorAvatar(videoDetail.avatar)})` }"></div>
                    <span class="author-name">{{ videoDetail.createBy || '未知用户' }}</span>
                  </div>
                  <span class="meta-v-separator"></span>
                  <div class="meta-date">
                    <i class="el-icon-date meta-icon"></i>
                    <span>{{ formatDate(videoDetail.createTime) }}</span>
                  </div>
                  <div v-if="videoTags.length" class="meta-tags">
                    <span
                      v-for="(tag, index) in videoTags"
                      :key="index"
                      class="tag"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>
                <div class="video-meta-right">
                  <div class="meta-stat-item">
                    <i class="el-icon-view stat-icon"></i>
                    <span>观看人数{{ formatCount(videoDetail.viewCount || 0) }}</span>
                  </div>
                  <span class="meta-dot"></span>
                  <div class="meta-stat-item">
                    <i class="el-icon-circle-check stat-icon"></i>
                    <span>完成人数{{ formatCount(completedCount) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI 内容摘要 -->
            <div class="ai-summary-section" v-if="videoDetail.videoDesc">
              <details class="summary-details" open>
                <summary class="summary-header">
                  <div class="summary-title">
                    <i class="el-icon-star"></i>
                    <span>AI 内容摘要</span>
                  </div>
                  <i class="el-icon-arrow-down expand-icon"></i>
                </summary>
                <div class="summary-content">
                  <mavon-editor
                    class="summary-markdown"
                    :value="videoDetail.videoDesc || ''"
                    :subfield="false"
                    :default-open="'preview'"
                    :toolbars-flag="false"
                    :editable="false"
                    :box-shadow="false"
                    :navigation="false"
                    :ishljs="false" />
                </div>
              </details>
            </div>

            <!-- 笔记（放在AI摘要和相关推荐之间，默认折叠） -->
            <div v-if="showNativeVideoPanels" class="notes-inline-section">
              <div class="notes-inline-header">
                <h3 class="section-title">笔记</h3>
                <button class="create-note-btn" @click="openCreateNoteDialog" v-permission="['system:article:add']">
                  <i class="el-icon-plus"></i>
                  创建笔记
                </button>
              </div>

              <div v-if="isLoadingNotes && mockNotes.length === 0" class="notes-loading">
                <span class="loading-text">加载中...</span>
              </div>

              <div v-else-if="!isLoadingNotes && mockNotes.length === 0" class="notes-empty">
                <i class="el-icon-document empty-icon"></i>
                <p class="empty-text">暂无笔记</p>
              </div>

              <div v-else class="notes-inline-list">
                <div
                  v-for="(note, index) in visibleNotes"
                  :key="note.id || index"
                  class="note-item"
                >
                  <div class="note-header-info">
                    <div class="note-avatar" :style="{ backgroundImage: `url(${getNoteAvatar(note.avatar)})` }"></div>
                    <div class="note-meta">
                      <span class="note-author">{{ note.createBy || '未知用户' }}</span>
                      <span class="note-timestamp">[{{ note.timestamp }}]</span>
                    </div>
                  </div>
                  <p class="note-content">{{ note.content }}</p>
                  <div v-if="note.thumbnail" class="note-screenshot" @click="previewImage(note.thumbnail)">
                    <img :src="getNoteThumbnail(note.thumbnail)" alt="笔记截图" />
                    <div class="screenshot-overlay">
                      <i class="el-icon-zoom-in"></i>
                    </div>
                  </div>
                  <hr v-if="index < visibleNotes.length - 1" class="note-divider" />
                </div>
              </div>

              <div v-if="mockNotes.length > 0" class="notes-inline-actions">
                <button
                  v-if="hasNotesToggle"
                  type="button"
                  class="notes-toggle-btn"
                  @click="toggleNotesExpanded"
                >
                  {{ notesExpanded ? '收起笔记' : `展开更多笔记 (${mockNotes.length}${hasMore ? '+' : ''})` }}
                </button>
                <button
                  v-if="notesExpanded && hasMore"
                  type="button"
                  class="notes-load-more-btn"
                  :disabled="isLoadingNotes"
                  @click="loadMoreNotes"
                >
                  {{ isLoadingNotes ? '加载中...' : '加载更多笔记' }}
                </button>
              </div>

              <div v-if="notesExpanded && !hasMore && mockNotes.length > notePreviewCount" class="notes-no-more">
                <span class="no-more-text">没有更多笔记了</span>
              </div>
            </div>

            <!-- 相关推荐 -->
            <div v-if="showNativeVideoPanels" class="related-section">
              <h3 class="section-title">相关推荐</h3>
              <div class="related-grid">
                <div
                  v-for="video in relatedVideos"
                  :key="video.id"
                  class="related-card"
                  @click="handleRelatedVideoClick(video.id)"
                >
                  <div class="related-thumbnail">
                    <img :src="video.thumbnail" :alt="video.title" />
                    <span class="video-duration">{{ video.duration }}</span>
                  </div>
                  <p class="related-title">{{ video.title }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧同分类最新视频 -->
          <aside v-if="showNativeVideoPanels" class="latest-panel">
            <div class="latest-container">
              <div class="latest-list">
                <div
                  v-for="video in categoryLatestVideos"
                  :key="video.id"
                  class="latest-item"
                  @click="handleRelatedVideoClick(video.id)"
                >
                  <div class="latest-thumbnail">
                    <img :src="video.thumbnail" :alt="video.title" />
                    <div class="latest-watch-status">
                      <WatchStatusBadge :status="video.watchStatus || '待观看'" />
                    </div>
                    <span class="video-duration">{{ video.duration }}</span>
                  </div>
                  <div class="latest-info">
                    <p class="latest-item-title">{{ video.title }}</p>
                    <div class="latest-item-meta">
                      <span>{{ formatCount(video.viewCount || 0) }}次观看</span>
                      <span>·</span>
                      <span>{{ formatDate(video.createTime) || '未知时间' }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="isLoadingCategoryLatest" class="notes-loading">
                  <span class="loading-text">加载中...</span>
                </div>
                <div v-if="!isLoadingCategoryLatest && categoryLatestVideos.length === 0" class="latest-empty">
                  <p class="empty-text">暂无最新视频</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>

    <!-- 图片预览弹框 -->
    <div v-if="showImagePreview" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-container">
        <img :src="previewImageUrl" alt="预览图" />
        <button class="image-preview-close" @click="closeImagePreview">
          <i class="el-icon-close"></i>
        </button>
      </div>
    </div>

    <!-- 创建笔记弹框 -->
    <div v-if="showNoteDialog" class="note-dialog-overlay" @click.self="closeNoteDialog">
      <div class="note-dialog">
        <!-- 弹框头部 -->
        <div class="note-dialog-header">
          <div class="note-dialog-user-info">
            <div class="note-dialog-avatar">
              <img :src="userAvatar" alt="Avatar" />
            </div>
            <div>
              <h3 class="note-dialog-username">{{ userName }}</h3>
              <div class="note-dialog-video-info">
                <i class="el-icon-video-play"></i>
                <span>{{ videoDetail.videoTitle }}</span>
              </div>
            </div>
          </div>
          <button class="note-dialog-close" @click="closeNoteDialog">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- 弹框内容 -->
        <div class="note-dialog-content">
          <!-- 时间点显示 -->
          <div class="note-time-badge">
            <i class="el-icon-date"></i>
            <span class="note-time-text">笔记时间点：{{ noteTimestamp }}</span>
          </div>

          <!-- 笔记内容 -->
          <div class="note-content-section">
            <h4 class="note-section-title">笔记内容</h4>
            <div class="note-textarea-wrapper">
              <textarea
                v-model="noteContent"
                class="note-textarea"
                placeholder="在此处输入您的笔记内容..."
                rows="8"
              ></textarea>
            </div>
          </div>

          <!-- 视频截图 -->
          <div class="note-screenshot-section">
            <h4 class="note-section-title">视频截图</h4>
            <div class="note-screenshot-preview">
              <img :src="screenshotDataUrl" alt="Video Screenshot" />
              <div class="screenshot-overlay-layer"></div>
              <div class="screenshot-time-badge">
                <i class="el-icon-picture"></i>
                自动截取于 {{ noteTimestamp }}
              </div>
            </div>
          </div>
        </div>

        <!-- 弹框底部 -->
        <div class="note-dialog-footer">
          <button class="note-btn-cancel" @click="closeNoteDialog">
            关闭
          </button>
          <button class="note-btn-save" @click="saveNote">
            <i class="el-icon-document"></i>
            保存笔记
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import { getVideoById, updateVideoViewCount, insertApeArticle, uploadFile, getApeArticlePage, getCompletedCountByVideoId } from '../../api/api'
import videoProgressMixin from '@/mixins/videoProgressMixin'
import {
  getVideoById,
  updateVideoViewCount,
  insertApeArticle,
  uploadFile,
  getApeArticlePage,
  getVideoManagePage,
  getVideoActivityPage,
  getUserInfo,
  getRelatedVideoList,
  getCompletedCountByVideoId,
  getMyTrainingPlanDetail,
  saveTrainingMaterialVideoProgress,
  getTrainingMaterialSyncVideoId,
  getApeTrainingMaterialById
} from '../../api/api'
import CommonHeader from '../../components/header/CommonHeader.vue'
import WatchStatusBadge from '../../components/video/WatchStatusBadge.vue'
import { mavonEditor } from 'mavon-editor'
import videojs from 'video.js'
import { MessageBox } from 'element-ui'
import { VIDEO_PLAYER_KEYBOARD_CONFIG } from '@/utils/videoPlayerConfig'
import { DEFAULT_AVATAR_PLACEHOLDER } from '@/utils/placeholders'
import 'mavon-editor/dist/css/index.css'

export default {
  name: 'VideoPlay',
  components: {
    CommonHeader,
    WatchStatusBadge,
    mavonEditor
  },
  mixins: [videoProgressMixin],
  data() {
    return {
      videoId: '',
      videoDetail: {},
      videoUrl: '',
      isPlaying: false,
      isMuted: false,
      volume: 80,
      currentTime: 0,
      duration: 0,
      playbackRate: 1,
      playbackRates: [1, 1.25, 1.5],
      progressPercent: 0,
      bufferedPercent: 0,
      // 视频完成人数
      completedCount: 0,
      // 笔记弹框相关
      showNoteDialog: false,
      noteContent: '',
      noteTimestamp: '',
      screenshotDataUrl: '',
      // 笔记列表
      mockNotes: [],
      // 分页相关
      pageNumber: 1,
      pageSize: 10,
      hasMore: true,
      isLoadingNotes: false,
      // 图片预览
      previewImageUrl: '',
      showImagePreview: false,
      // 相关推荐视频数据
      relatedVideos: [],
      // 同分类最新视频（右侧）
      categoryLatestVideos: [],
      isLoadingCategoryLatest: false,
      // 笔记折叠（默认展示5条）
      notePreviewCount: 5,
      notesExpanded: false,
      // 标记是否来自公区视频库
      fromPublic: false,
      // 标记是否来自部门视频库
      fromDepartment: false,
      // 标记是否来自培训资料
      fromTraining: false,
      // 培训资料直播放模式
      materialMode: false,
      // 培训资料来源分类信息（用于面包屑返回）
      trainingClassificationId: '',
      trainingClassificationName: '',
      // 培训计划上下文（用于防跳级校验）
      trainingPlanId: '',
      trainingStageId: '',
      trainingMaterialId: '',
      materialActualPlayTime: 0,
      materialPlayTimer: null,
      materialProgressTimer: null,
      materialProgressSaving: false,
      materialLastReportTime: 0,
      materialAutoCompleted: false,
      // 是否为移动端页面
      isMobilePage: false,
      // 移动端控制栏显示状态
      showMobileControls: true,
      // 移动端控制栏自动隐藏定时器
      mobileControlsHideTimer: null,
      // 移动端播放后自动隐藏控制栏延迟（毫秒）
      mobileControlsHideDelay: 3000,
      // 是否已锁定屏幕方向
      isOrientationLocked: false,
      isNativeFullscreenActive: false,
      isMobileCssFullscreen: false,
      isVideoLoading: false,
      isPlaybackWaiting: false,
      videoLoadingText: '视频加载中...',
      videoLoadError: '',
      networkFailureCount: 0,
      networkAlertTitle: '',
      hasShownNetworkFailureDialog: false,
      maxNetworkFailureRetries: 5,
      videoErrorRetryCount: 0,
      maxVideoErrorRetries: 5,
      isRecoveringVideo: false,
      pendingPlayAfterCanPlay: false,
      videoJsPlayer: null,
      isProgressDragging: false,
      hasVideoFrameReady: false,
      posterLoadFailed: false,
      disableCrossOriginMedia: false,
      mobileSeekHoldTimer: null,
      mobileSeekRepeatTimer: null,
      mobileSeekTouchIdentifier: null,
      mobileSeekActiveDirection: '',
      mobileSeekTriggered: false,
      mobileSeekHoldDelay: 320,
      mobileSeekStepSeconds: 5,
      mobileSeekRepeatInterval: 260
    }
  },
  computed: {
    videoTags() {
      if (!this.videoDetail.tagNames) return []
      return this.videoDetail.tagNames.split(',').filter(tag => tag.trim())
    },
    currentTimeFormatted() {
      return this.formatTime(this.currentTime)
    },
    durationFormatted() {
      return this.formatTime(this.duration)
    },
    videoPosterUrl() {
      if (!this.videoDetail.videoCover) return ''
      return this.getVideoUrl(this.videoDetail.videoCover)
    },
    videoCrossOriginAttr() {
      const mediaUrl = this.videoUrl || this.getVideoUrl(this.videoDetail.videoUrl)
      if (!mediaUrl || this.disableCrossOriginMedia) {
        return null
      }
      return this.isCrossOriginExternalUrl(mediaUrl) ? 'anonymous' : null
    },
    showNativeVideoPanels() {
      return !this.materialMode || !!this.videoId
    },
    showPosterLayer() {
      return !!this.videoPosterUrl && !this.posterLoadFailed && !this.hasVideoFrameReady && !this.videoLoadError
    },
    shouldShowNetworkAlert() {
      return !!this.networkAlertTitle &&
        this.networkFailureCount > 0 &&
        this.networkFailureCount <= this.maxNetworkFailureRetries &&
        !this.isNativeFullscreenActive &&
        !this.isMobileCssFullscreen
    },
    controlsPanelStyle() {
      if (!this.isMobilePage) return null
      return {
        padding: '4px 6px',
        opacity: this.showMobileControls ? 1 : 0,
        pointerEvents: this.showMobileControls ? 'auto' : 'none'
      }
    },
    progressBarStyle() {
      if (!this.isMobilePage) return null
      return {
        height: '3px',
        marginBottom: '4px',
        pointerEvents: 'auto',
        cursor: 'pointer'
      }
    },
    controlsBottomStyle() {
      if (!this.isMobilePage) return null
      return {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '4px'
      }
    },
    controlsGroupStyle() {
      if (!this.isMobilePage) return null
      return {
        width: 'auto',
        gap: '4px',
        flexWrap: 'nowrap'
      }
    },
    controlBtnStyle() {
      if (!this.isMobilePage) return null
      return {
        minWidth: '26px',
        height: '26px',
        borderRadius: '6px',
        background: 'rgba(15, 23, 42, 0.48)'
      }
    },
    controlIconStyle() {
      if (!this.isMobilePage) return null
      return {
        fontSize: '13px'
      }
    },
    volumeSliderStyle() {
      if (!this.isMobilePage) return null
      return {
        width: '0'
      }
    },
    timeDisplayStyle() {
      if (!this.isMobilePage) return null
      return {
        marginLeft: '4px',
        fontSize: '10px'
      }
    },
    playbackRateStyle() {
      if (!this.isMobilePage) return null
      return {
        gap: '0',
        padding: '0',
        borderRadius: '0',
        background: 'transparent'
      }
    },
    isMobileFullscreenActive() {
      return this.isMobilePage && (this.isNativeFullscreenActive || this.isMobileCssFullscreen)
    },
    showMobileSeekZones() {
      return this.isMobileFullscreenActive &&
        !!this.videoUrl &&
        !this.isVideoLoading &&
        !this.videoLoadError &&
        !this.showImagePreview &&
        !this.showNoteDialog
    },
    mobileSeekIndicatorText() {
      if (this.mobileSeekActiveDirection === 'forward') {
        return `长按快进中 · 每次 +${this.mobileSeekStepSeconds} 秒`
      }
      if (this.mobileSeekActiveDirection === 'backward') {
        return `长按快退中 · 每次 -${this.mobileSeekStepSeconds} 秒`
      }
      return ''
    },
    // 获取当前登录用户信息
    currentUser() {
      return this.$store.getters.getUserInfo || JSON.parse(localStorage.getItem('user_info') || '{}')
    },
    // 用户名
    userName() {
      return this.currentUser.userName || this.currentUser.nickName || '未知用户'
    },
    // 用户头像
    userAvatar() {
      if (this.currentUser.avatar) {
        // 如果头像是完整URL，直接使用
        if (this.currentUser.avatar.startsWith('http')) {
          return this.currentUser.avatar
        }
        // 否则拼接服务器地址
        return this.$store.state.HOST + this.currentUser.avatar
      }
      // 默认头像
      return DEFAULT_AVATAR_PLACEHOLDER
    },
    visibleNotes() {
      if (this.notesExpanded) {
        return this.mockNotes
      }
      return this.mockNotes.slice(0, this.notePreviewCount)
    },
    hasNotesToggle() {
      return this.mockNotes.length > this.notePreviewCount || this.hasMore
    }
  },
  methods: {
    // 获取当前视频元素（PC端或移动端）
    getVideoElement() {
      return this.$refs.videoPlayer || null
    },

    getVideoJsPlayer() {
      if (this.videoJsPlayer && typeof this.videoJsPlayer.isDisposed === 'function' && !this.videoJsPlayer.isDisposed()) {
        return this.videoJsPlayer
      }
      return null
    },

    isNetworkConnectionError(error) {
      const statusCode = Number(error && error.response && error.response.status)
      const errorCode = String((error && error.code) || '').toUpperCase()
      const message = String((error && error.message) || error || '').toLowerCase()
      const hasAxiosShape = !!(error && (error.isAxiosError || error.config || error.request))

      if (errorCode === 'ECONNABORTED') {
        return true
      }

      if (
        message.includes('network error') ||
        message.includes('timeout') ||
        message.includes('failed to fetch') ||
        message.includes('load failed')
      ) {
        return true
      }

      if (!statusCode) {
        return hasAxiosShape
      }

      return statusCode >= 500 || statusCode === 408 || statusCode === 429
    },

    resetPlaybackNetworkState() {
      this.networkFailureCount = 0
      this.networkAlertTitle = ''
      this.hasShownNetworkFailureDialog = false
    },

    registerPlaybackNetworkFailure() {
      this.networkFailureCount += 1

      if (this.networkFailureCount <= this.maxNetworkFailureRetries) {
        this.networkAlertTitle = `网络连接异常，正在重试（${this.networkFailureCount}/${this.maxNetworkFailureRetries}）`
        return
      }

      this.networkAlertTitle = ''
      if (!this.hasShownNetworkFailureDialog) {
        this.hasShownNetworkFailureDialog = true
        MessageBox.alert('网络连接出错，请刷新重试', '错误', {
          confirmButtonText: '确定',
          type: 'error'
        }).catch(() => {})
      }
    },

    onPlaybackNetworkError(error) {
      if (!this.isNetworkConnectionError(error)) {
        return
      }
      this.registerPlaybackNetworkFailure()
    },

    onPlaybackNetworkRecovered() {
      this.resetPlaybackNetworkState()
    },

    clampNumber(value, min, max) {
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) {
        return min
      }
      return Math.min(Math.max(numericValue, min), max)
    },

    getPlaybackDuration() {
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      const duration = player && typeof player.duration === 'function'
        ? Number(player.duration())
        : Number(video && video.duration)

      if (!Number.isFinite(duration) || duration <= 0) {
        return 0
      }
      return duration
    },

    syncPlaybackProgress(currentTime, duration = this.getPlaybackDuration()) {
      const safeDuration = Number.isFinite(Number(duration)) && Number(duration) > 0 ? Number(duration) : 0
      const safeCurrentTime = safeDuration > 0
        ? this.clampNumber(currentTime, 0, safeDuration)
        : 0

      this.currentTime = safeCurrentTime
      this.duration = safeDuration
      this.progressPercent = safeDuration ? (safeCurrentTime / safeDuration) * 100 : 0
    },

    updateBufferedProgress() {
      const video = this.getVideoElement()
      if (!video) {
        this.bufferedPercent = 0
        return
      }

      const duration = Number(video.duration)
      if (!Number.isFinite(duration) || duration <= 0) {
        this.bufferedPercent = 0
        return
      }

      const buffered = video.buffered
      if (!buffered || buffered.length === 0) {
        this.bufferedPercent = 0
        return
      }

      const currentTime = Number(video.currentTime) || 0
      let activeRangeEnd = 0
      let maxRangeEnd = 0

      for (let i = 0; i < buffered.length; i++) {
        const rangeStart = Number(buffered.start(i)) || 0
        const rangeEnd = Number(buffered.end(i)) || 0
        maxRangeEnd = Math.max(maxRangeEnd, rangeEnd)
        if (currentTime >= rangeStart && currentTime <= rangeEnd) {
          activeRangeEnd = rangeEnd
        }
      }

      const bufferedEnd = activeRangeEnd || maxRangeEnd
      const nextBufferedPercent = (bufferedEnd / duration) * 100
      this.bufferedPercent = this.clampNumber(nextBufferedPercent, 0, 100)
    },

    setPlaybackPosition(time) {
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      const duration = this.getPlaybackDuration()
      if (!video || !duration) return

      const targetTime = this.clampNumber(time, 0, duration)
      if (player && typeof player.currentTime === 'function') {
        player.currentTime(targetTime)
      } else {
        video.currentTime = targetTime
      }

      this.syncPlaybackProgress(targetTime, duration)
    },

    seekByDelta(delta) {
      const video = this.getVideoElement()
      if (!video) return
      const currentTime = Number(video.currentTime) || 0
      this.setPlaybackPosition(currentTime + delta)
    },

    applyVolume(volumePercent) {
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      if (!video) return

      const nextVolume = this.clampNumber(volumePercent, 0, 100)
      this.volume = nextVolume

      if (player) {
        player.volume(nextVolume / 100)
        player.muted(nextVolume === 0)
        this.isMuted = player.muted()
      } else {
        video.volume = nextVolume / 100
        video.muted = nextVolume === 0
        this.isMuted = video.muted
      }
    },

    adjustVolumeByStep(step) {
      this.applyVolume((Number(this.volume) || 0) + step)
    },

    getProgressRatioByClientX(clientX) {
      const progressBar = this.$refs.progressBarWrapper
      if (!progressBar) return null

      const rect = progressBar.getBoundingClientRect()
      if (!rect.width) return null

      return this.clampNumber((clientX - rect.left) / rect.width, 0, 1)
    },

    seekToClientX(clientX) {
      const ratio = this.getProgressRatioByClientX(clientX)
      const duration = this.getPlaybackDuration()
      if (ratio === null || !duration) return

      this.setPlaybackPosition(duration * ratio)
      if (this.isMobilePage) {
        this.showMobileControlsTemporarily()
      }
    },

    cleanupProgressDrag() {
      this.isProgressDragging = false
      document.removeEventListener('mousemove', this.handleProgressMouseMove)
      document.removeEventListener('mouseup', this.handleProgressMouseUp)
      document.removeEventListener('touchmove', this.handleProgressTouchMove, { passive: false })
      document.removeEventListener('touchend', this.handleProgressTouchEnd)
      document.removeEventListener('touchcancel', this.handleProgressTouchCancel)
    },

    handleProgressMouseDown(event) {
      if (!event || event.button !== 0) return

      this.cleanupProgressDrag()
      this.isProgressDragging = true
      this.seekToClientX(event.clientX)
      document.addEventListener('mousemove', this.handleProgressMouseMove)
      document.addEventListener('mouseup', this.handleProgressMouseUp)
    },

    handleProgressMouseMove(event) {
      if (!this.isProgressDragging) return
      this.seekToClientX(event.clientX)
    },

    handleProgressMouseUp(event) {
      if (!this.isProgressDragging) return
      this.seekToClientX(event.clientX)
      this.cleanupProgressDrag()
    },

    handleProgressTouchStart(event) {
      const touch = event && event.touches ? event.touches[0] : null
      if (!touch) return

      this.cleanupProgressDrag()
      this.isProgressDragging = true
      this.seekToClientX(touch.clientX)
      document.addEventListener('touchmove', this.handleProgressTouchMove, { passive: false })
      document.addEventListener('touchend', this.handleProgressTouchEnd)
      document.addEventListener('touchcancel', this.handleProgressTouchCancel)
    },

    handleProgressTouchMove(event) {
      if (!this.isProgressDragging) return

      const touch = event && event.touches ? event.touches[0] : null
      if (!touch) return

      event.preventDefault()
      this.seekToClientX(touch.clientX)
    },

    handleProgressTouchEnd(event) {
      if (!this.isProgressDragging) return

      const touch = event && event.changedTouches ? event.changedTouches[0] : null
      if (touch) {
        this.seekToClientX(touch.clientX)
      }
      this.cleanupProgressDrag()
    },

    handleProgressTouchCancel() {
      this.cleanupProgressDrag()
    },

    shouldIgnoreKeyboardShortcut(event) {
      if (!event || event.altKey || event.ctrlKey || event.metaKey) {
        return true
      }

      if (this.showNoteDialog || this.showImagePreview) {
        return true
      }

      const target = event.target
      if (!target) {
        return false
      }

      const tagName = target.tagName ? target.tagName.toLowerCase() : ''
      if (['input', 'textarea', 'select', 'button'].includes(tagName)) {
        return true
      }

      if (target.isContentEditable) {
        return true
      }

      if (typeof target.closest === 'function' && target.closest('.v-note-wrapper, .el-message-box__wrapper')) {
        return true
      }

      return false
    },

    handleGlobalKeydown(event) {
      if (!this.videoUrl || !this.getVideoElement() || this.shouldIgnoreKeyboardShortcut(event)) {
        return
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          this.seekByDelta(-VIDEO_PLAYER_KEYBOARD_CONFIG.seekStep)
          break
        case 'ArrowRight':
          event.preventDefault()
          this.seekByDelta(VIDEO_PLAYER_KEYBOARD_CONFIG.seekStep)
          break
        case 'ArrowUp':
          event.preventDefault()
          this.adjustVolumeByStep(VIDEO_PLAYER_KEYBOARD_CONFIG.volumeStep)
          break
        case 'ArrowDown':
          event.preventDefault()
          this.adjustVolumeByStep(-VIDEO_PLAYER_KEYBOARD_CONFIG.volumeStep)
          break
        default:
          return
      }

      if (this.isMobilePage) {
        this.showMobileControlsTemporarily()
      }
    },

    getVideoMimeType(url) {
      if (!url) return ''
      const cleanUrl = url.split('?')[0].split('#')[0]
      const ext = cleanUrl.includes('.') ? cleanUrl.substring(cleanUrl.lastIndexOf('.') + 1).toLowerCase() : ''
      const mimeMap = {
        mp4: 'video/mp4',
        m4v: 'video/mp4',
        webm: 'video/webm',
        ogg: 'video/ogg',
        ogv: 'video/ogg',
        mov: 'video/quicktime',
        m3u8: 'application/x-mpegURL',
        mpd: 'application/dash+xml'
      }
      return mimeMap[ext] || ''
    },

    isCrossOriginExternalUrl(url) {
      if (!url) return false
      try {
        const resolvedUrl = new URL(url, window.location.origin)
        return resolvedUrl.origin !== window.location.origin
      } catch (error) {
        return false
      }
    },

    initializeVideoJsPlayer() {
      const existingPlayer = this.getVideoJsPlayer()
      if (existingPlayer) {
        return existingPlayer
      }

      const videoElement = this.getVideoElement()
      if (!videoElement) {
        return null
      }

      this.videoJsPlayer = videojs(videoElement, {
        controls: false,
        autoplay: false,
        preload: 'auto',
        fluid: false,
        muted: this.isMuted,
        controlBar: false,
        bigPlayButton: false,
        errorDisplay: false,
        loadingSpinner: false,
        playbackRates: this.playbackRates,
        html5: {
          nativeTextTracks: false,
          nativeAudioTracks: false,
          nativeVideoTracks: false,
          vhs: {
            overrideNative: !videojs.browser.IS_SAFARI
          }
        }
      })

      return this.videoJsPlayer
    },

    disposeVideoJsPlayer() {
      const player = this.getVideoJsPlayer()
      if (player) {
        player.dispose()
      }
      this.videoJsPlayer = null
    },

    applyVideoSource(url) {
      const player = this.initializeVideoJsPlayer()
      if (!player || !url) {
        return
      }

      const source = { src: url }
      const type = this.getVideoMimeType(url)
      if (type) {
        source.type = type
      }

      player.src(source)
      player.muted(this.isMuted)
      player.volume(this.volume / 100)
      player.playbackRate(this.playbackRate)
      player.load()
    },

    resetMaterialProgressState() {
      this.stopMaterialPlayTimer()
      this.stopMaterialProgressTimer()
      this.materialActualPlayTime = 0
      this.materialLastReportTime = 0
      this.materialProgressSaving = false
      this.materialAutoCompleted = false
    },

    startMaterialPlayTimer() {
      if (!this.materialMode || this.materialPlayTimer) {
        return
      }
      this.materialPlayTimer = setInterval(() => {
        this.materialActualPlayTime += 1
      }, 1000)
    },

    stopMaterialPlayTimer() {
      if (this.materialPlayTimer) {
        clearInterval(this.materialPlayTimer)
        this.materialPlayTimer = null
      }
    },

    startMaterialProgressTimer() {
      if (!this.materialMode || this.materialProgressTimer) {
        return
      }
      this.materialProgressTimer = setInterval(() => {
        this.reportTrainingMaterialProgress()
      }, 15000)
    },

    stopMaterialProgressTimer() {
      if (this.materialProgressTimer) {
        clearInterval(this.materialProgressTimer)
        this.materialProgressTimer = null
      }
    },

    async reportTrainingMaterialProgress(force = false) {
      if (!this.materialMode || this.materialProgressSaving || this.materialAutoCompleted) {
        return
      }
      if (!this.trainingPlanId || !this.trainingStageId || !this.trainingMaterialId) {
        return
      }
      const video = this.getVideoElement()
      if (!video) {
        return
      }
      const currentTime = Number(video.currentTime || 0)
      const duration = Number(video.duration || 0)
      if (!duration || isNaN(duration) || currentTime < 0 || isNaN(currentTime)) {
        return
      }
      if (!force && video.paused) {
        return
      }
      if (!force && Math.abs(currentTime - this.materialLastReportTime) < 10) {
        return
      }

      this.materialProgressSaving = true
      try {
        const progress = Math.max(0, Math.min(100, Number(((currentTime / duration) * 100).toFixed(2))))
        const res = await saveTrainingMaterialVideoProgress({
          planId: this.trainingPlanId,
          stageId: this.trainingStageId,
          materialId: this.trainingMaterialId,
          currentTime: Number(currentTime.toFixed(2)),
          duration: Number(duration.toFixed(2)),
          progress,
          actualPlayTime: this.materialActualPlayTime
        })
        if (res.code === 1000) {
          this.materialLastReportTime = currentTime
          this.materialActualPlayTime = 0
          this.onPlaybackNetworkRecovered()
          if (progress >= 95) {
            this.materialAutoCompleted = true
            this.$message.success('资料学习完成，已满足解锁条件')
          }
        }
      } catch (error) {
        console.error('上报培训资料视频进度失败:', error)
        this.onPlaybackNetworkError(error)
      } finally {
        this.materialProgressSaving = false
      }
    },

    // 按设备能力判断移动端：避免普通 PC 误判，同时兼容 UA 不标准的触摸设备
    detectMobilePage() {
      const ua = window.navigator.userAgent.toLowerCase()
      const isMobileUA = /android|iphone|ipad|ipod|mobile|iemobile|windows phone/i.test(ua)
      const isTouchDevice = navigator.maxTouchPoints > 0 || 'ontouchstart' in window
      const isTouchMac = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1
      const isTouchNarrowScreen = isTouchDevice && window.innerWidth <= 1024
      this.isMobilePage = isMobileUA || isTouchMac || isTouchNarrowScreen
    },

    handleViewportChange() {
      const wasMobilePage = this.isMobilePage
      this.detectMobilePage()
      if (wasMobilePage && !this.isMobilePage) {
        this.exitMobileCssFullscreen()
        this.showMobileControls = true
        this.clearMobileControlsHideTimer()
      }
      if (!wasMobilePage && this.isMobilePage && this.isPlaying) {
        this.showMobileControls = true
        this.startMobileControlsAutoHide()
      }
    },

    clearMobileControlsHideTimer() {
      if (this.mobileControlsHideTimer) {
        clearTimeout(this.mobileControlsHideTimer)
        this.mobileControlsHideTimer = null
      }
    },

    startMobileControlsAutoHide() {
      if (!this.isMobilePage) return
      this.clearMobileControlsHideTimer()
      if (!this.isPlaying) return

      this.mobileControlsHideTimer = setTimeout(() => {
        if (this.isMobilePage && this.isPlaying) {
          this.showMobileControls = false
        }
      }, this.mobileControlsHideDelay)
    },

    showMobileControlsTemporarily() {
      if (!this.isMobilePage) return
      this.showMobileControls = true
      this.startMobileControlsAutoHide()
    },

    handleControlInteraction() {
      if (!this.isMobilePage) return
      this.showMobileControlsTemporarily()
    },

    handlePlayerAreaClick(event) {
      if (!this.isMobilePage) return

      const controls = this.$refs.videoControls
      if (controls && event && controls.contains(event.target)) {
        return
      }

      if (!this.isPlaying) {
        this.showMobileControls = true
        this.clearMobileControlsHideTimer()
        return
      }

      this.showMobileControlsTemporarily()
    },

    handleMobileGestureZoneTap() {
      if (!this.isMobilePage) return
      if (!this.isPlaying) {
        this.showMobileControls = true
        this.clearMobileControlsHideTimer()
        return
      }
      this.showMobileControlsTemporarily()
    },

    clearMobileSeekHoldTimer() {
      if (this.mobileSeekHoldTimer) {
        clearTimeout(this.mobileSeekHoldTimer)
        this.mobileSeekHoldTimer = null
      }
    },

    clearMobileSeekRepeatTimer() {
      if (this.mobileSeekRepeatTimer) {
        clearInterval(this.mobileSeekRepeatTimer)
        this.mobileSeekRepeatTimer = null
      }
    },

    stopMobileSeekGesture() {
      this.clearMobileSeekHoldTimer()
      this.clearMobileSeekRepeatTimer()
      this.mobileSeekTouchIdentifier = null
      this.mobileSeekActiveDirection = ''
      this.mobileSeekTriggered = false
    },

    startMobileSeekGesture(direction) {
      if (!this.showMobileSeekZones) return

      const delta = direction === 'forward' ? this.mobileSeekStepSeconds : -this.mobileSeekStepSeconds
      this.mobileSeekTriggered = true
      this.mobileSeekActiveDirection = direction
      this.seekByDelta(delta)
      this.mobileSeekRepeatTimer = setInterval(() => {
        this.seekByDelta(delta)
      }, this.mobileSeekRepeatInterval)
    },

    handleMobileSeekTouchStart(direction, event) {
      if (!this.showMobileSeekZones) return

      const touch = event && event.changedTouches ? event.changedTouches[0] : (event && event.touches ? event.touches[0] : null)
      if (!touch) return

      this.stopMobileSeekGesture()
      this.mobileSeekTouchIdentifier = touch.identifier
      this.mobileSeekHoldTimer = setTimeout(() => {
        if (this.mobileSeekTouchIdentifier === touch.identifier) {
          this.startMobileSeekGesture(direction)
        }
      }, this.mobileSeekHoldDelay)
    },

    handleMobileSeekTouchEnd(event) {
      if (this.mobileSeekTouchIdentifier !== null && event && event.changedTouches && event.changedTouches.length) {
        const touched = Array.from(event.changedTouches).some(touch => touch.identifier === this.mobileSeekTouchIdentifier)
        if (!touched) return
      }

      const hasTriggeredSeek = this.mobileSeekTriggered
      this.stopMobileSeekGesture()
      if (hasTriggeredSeek) {
        this.showMobileControlsTemporarily()
      }
    },

    handleMobileSeekTouchCancel() {
      this.stopMobileSeekGesture()
    },

    getRateBtnStyle(rate) {
      if (!this.isMobilePage) return null
      const style = {
        fontSize: '11px',
        padding: '2px 4px',
        borderRadius: '6px',
        lineHeight: '1'
      }
      if (this.playbackRate === rate) {
        style.background = 'rgba(26, 113, 255, 0.2)'
        style.color = '#1a71ff'
        style.textDecoration = 'none'
      }
      return style
    },

    // 获取面包屑标题
    getBreadcrumbTitle() {
      if (this.fromPublic) {
        return '视频播放'
      } else if (this.fromDepartment) {
        return '视频播放'
      } else if (this.fromTraining) {
        return '视频播放'
      } else {
        return '集中学习打卡'
      }
    },

    getTrainingBackRoute() {
      if (this.trainingPlanId) {
        return {
          path: '/trainingPlanGuide',
          query: {
            planId: this.trainingPlanId,
            stageId: this.trainingStageId || '',
            materialId: this.trainingMaterialId || ''
          }
        }
      }
      return {
        path: '/trainingMaterials',
        query: {
          classificationId: this.trainingClassificationId || '',
          classificationName: this.trainingClassificationName || '',
          mark: '2'
        }
      }
    },

    buildTrainingMaterialVideoDetail(material = null) {
      const source = material || {}
      return {
        videoTitle: source.title || this.$route.query.title || '培训视频',
        createBy: source.createBy || '',
        createTime: source.createTime || '',
        tagNames: source.tagNames || '',
        viewCount: Number(source.viewCount) || 0,
        videoDesc: source.materialDesc || '',
        videoCover: source.coverImage || ''
      }
    },

    initRouteContext() {
      const fromSource = this.$route.query.from
      this.fromPublic = fromSource === 'public'
      this.fromDepartment = fromSource === 'department'
      this.fromTraining = fromSource === 'training'
      this.trainingClassificationId = this.$route.query.classificationId || ''
      this.trainingClassificationName = this.$route.query.classificationName || ''
      this.trainingPlanId = this.$route.query.planId || ''
      this.trainingStageId = this.$route.query.stageId || ''
      this.trainingMaterialId = this.$route.query.materialId || ''
      this.resetMaterialProgressState()

      if (this.fromTraining && (this.$route.query.fileUrl || this.trainingMaterialId)) {
        this.materialMode = true
        this.videoId = this.$route.query.id || this.$route.query.syncVideoId || ''
        this.videoDetail = this.buildTrainingMaterialVideoDetail()
        this.videoUrl = ''
        this.relatedVideos = []
        this.categoryLatestVideos = []
        this.mockNotes = []
        this.hasMore = false
        this.isLoadingNotes = false
        this.notesExpanded = false
        this.completedCount = 0
        return true
      }

      this.materialMode = false
      return false
    },

    async validateTrainingAccessInPlan() {
      if (!this.fromTraining || !this.materialMode || !this.trainingPlanId || !this.trainingMaterialId) {
        return false
      }
      try {
        const res = await getMyTrainingPlanDetail({ planId: this.trainingPlanId })
        if (res.code !== 1000 || !res.data) {
          return false
        }
        const stageList = res.data.stageList || []
        for (let i = 0; i < stageList.length; i += 1) {
          const stage = stageList[i]
          const materialList = stage.materialList || []
          const targetRel = materialList.find(rel => {
            const relMaterialId = rel.materialId || (rel.material && rel.material.id) || ''
            return String(relMaterialId) === String(this.trainingMaterialId)
          })
          if (!targetRel) {
            continue
          }
          if (res.data.planOverdueFlag === '1' && res.data.planUserStatus !== '1' && targetRel.userStatus !== '1') {
            this.$message.warning('当前培训计划已逾期，未完成资料不可继续计入完成进度')
            this.$router.replace({
              path: '/trainingPlanGuide',
              query: {
                planId: this.trainingPlanId,
                stageId: stage.id || '',
                materialId: this.trainingMaterialId
              }
            })
            return true
          }
          if (stage.stageStatus === '0') {
            this.$message.warning(stage.lockReason || '该资料所属章节未解锁，请按章节顺序学习')
            this.$router.replace({
              path: '/trainingPlanGuide',
              query: {
                planId: this.trainingPlanId,
                stageId: stage.id || '',
                materialId: this.trainingMaterialId
              }
            })
            return true
          }
        }
      } catch (error) {
        console.error('校验培训计划资料权限失败:', error)
      }
      return false
    },

    async loadTrainingMaterialDetail() {
      this.videoDetail = this.buildTrainingMaterialVideoDetail()
      if (!this.trainingMaterialId) {
        return this.$route.query.fileUrl || ''
      }
      try {
        const res = await getApeTrainingMaterialById({ id: this.trainingMaterialId })
        if (res.code === 1000 && res.data) {
          if (!this.videoId && res.data.videoId) {
            this.videoId = String(res.data.videoId)
          }
          this.videoDetail = this.buildTrainingMaterialVideoDetail(res.data)
          return res.data.fileUrl || this.$route.query.fileUrl || ''
        }
      } catch (error) {
        console.error('加载培训资料详情失败:', error)
      }
      return this.$route.query.fileUrl || ''
    },

    async resolveTrainingMaterialSyncVideoId() {
      if (this.videoId || !this.trainingPlanId || !this.trainingStageId || !this.trainingMaterialId) {
        return this.videoId
      }
      try {
        const res = await getTrainingMaterialSyncVideoId({
          planId: this.trainingPlanId,
          stageId: this.trainingStageId,
          materialId: this.trainingMaterialId
        })
        if (res.code === 1000 && res.data) {
          this.videoId = String(res.data)
        }
      } catch (error) {
        console.error('获取培训资料同步视频ID失败:', error)
      }
      return this.videoId
    },

    async initializeTrainingMaterialPlayback() {
      if (!this.fromTraining || !this.materialMode) {
        return
      }

      const currentRoutePath = this.$route.fullPath
      const fileUrl = await this.loadTrainingMaterialDetail()
      await this.resolveTrainingMaterialSyncVideoId()

      if (currentRoutePath !== this.$route.fullPath || !this.fromTraining || !this.materialMode) {
        return
      }

      if (this.videoId) {
        const loaded = await this.loadVideoDetail()
        if (loaded) {
          await this.loadNoteList()
          return
        }
      }

      const normalizedUrl = this.getVideoUrl(fileUrl)
      await this.setVideoSource(normalizedUrl)
    },

    // 加载视频详情
    async loadVideoDetail() {
      if (this.materialMode && !this.videoId) {
        return false
      }
      this.isVideoLoading = true
      this.videoLoadError = ''
      try {
        const res = await getVideoById({ id: this.videoId })
        if (res.code === 1000 && res.data) {
          this.videoDetail = res.data
          const newUrl = this.getVideoUrl(res.data.videoUrl)
          await this.setVideoSource(newUrl)
          // 加载完成人数
          this.loadCompletedCount()
          // 加载相关推荐视频
          this.loadRelatedVideos()
          // 加载同分类最新视频
          this.loadCategoryLatestVideos()
          return true
        } else {
          this.isVideoLoading = false
          this.$message.error('视频加载失败')
          return false
        }
      } catch (error) {
        console.error('加载视频详情失败:', error)
        this.isVideoLoading = false
        this.$message.error('视频加载失败')
        return false
      }
    },

    // 获取视频完成人数
    async loadCompletedCount() {
      if (!this.videoId) {
        return
      }
      try {
        const res = await getCompletedCountByVideoId({ videoId: this.videoId })
        if (res.code === 1000) {
          this.completedCount = res.data || 0
        }
      } catch (error) {
        console.error('获取视频完成人数失败:', error)
      }
    },

    // 获取视频URL
    getVideoUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      if (process.env.NODE_ENV === 'development') {
        const baseApi = process.env.VUE_APP_BASE_API || '/api'
        return `${baseApi}${normalizedPath}`
      }

      return `${this.$store.state.HOST}${normalizedPath}`
    },

    resetVideoPlaybackState(options = {}) {
      const { preserveRetryCount = false, preserveCrossOriginMode = false } = options
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
      this.progressPercent = 0
      this.bufferedPercent = 0
      this.isVideoLoading = false
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.videoLoadError = ''
      this.pendingPlayAfterCanPlay = false
      this.hasVideoFrameReady = false
      this.posterLoadFailed = false
      if (!preserveRetryCount) {
        this.videoErrorRetryCount = 0
        this.resetPlaybackNetworkState()
      }
      if (!preserveCrossOriginMode) {
        this.disableCrossOriginMedia = false
      }
      this.isRecoveringVideo = false
      this.clearMobileControlsHideTimer()
      this.showMobileControls = true
    },

    async setVideoSource(url, options = {}) {
      const player = this.getVideoJsPlayer()
      if (player) {
        player.pause()
      }

      if (typeof this.cleanupVideoProgress === 'function') {
        this.cleanupVideoProgress()
      }
      if (typeof this.resetVideoProgressState === 'function') {
        this.resetVideoProgressState()
      }

      this.resetVideoPlaybackState(options)
      this.videoUrl = url || ''
      this.isVideoLoading = !!url
      if (url) {
        await this.$nextTick()
        this.applyVideoSource(url)
      } else {
        this.videoLoadError = '视频地址不存在'
      }
    },

    async reloadVideoSource() {
      if (!this.videoUrl) {
        return false
      }

      const currentUrl = this.videoUrl
      await this.setVideoSource(currentUrl, {
        preserveRetryCount: true,
        preserveCrossOriginMode: this.disableCrossOriginMedia
      })
      return true
    },

    async fallbackToCompatibilityPlayback(reason) {
      if (!this.videoUrl || this.disableCrossOriginMedia || !this.isCrossOriginExternalUrl(this.videoUrl)) {
        return false
      }

      console.warn('跨域截图模式加载失败，回退到兼容播放模式:', reason, this.videoUrl)
      this.disableCrossOriginMedia = true
      await this.setVideoSource(this.videoUrl, {
        preserveRetryCount: true,
        preserveCrossOriginMode: true
      })
      return true
    },

    async recoverVideoPlayback(reason) {
      if (!this.videoUrl || this.isRecoveringVideo || this.videoErrorRetryCount >= this.maxVideoErrorRetries) {
        return false
      }

      const video = this.getVideoElement()
      this.isRecoveringVideo = true
      this.videoErrorRetryCount += 1
      this.pendingPlayAfterCanPlay = !!(video && !video.paused)

      try {
        console.warn('视频播放异常，尝试自动恢复:', reason, this.videoUrl)
        return await this.reloadVideoSource()
      } finally {
        this.isRecoveringVideo = false
      }
    },

    async safePlay(options = {}) {
      const { allowRecover = true } = options
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      if (!video || !this.videoUrl) {
        return
      }

      try {
        if (player) {
          if ((!video.currentSrc && this.videoUrl) || video.networkState === 3) {
            this.applyVideoSource(this.videoUrl)
          }
          await player.play()
        } else {
          if (!video.currentSrc || video.networkState === 3) {
            video.load()
          }
          await video.play()
        }
        this.videoLoadError = ''
        this.onPlaybackNetworkRecovered()
      } catch (error) {
        console.error('视频播放失败:', error)
        if (allowRecover && error && error.name === 'NotSupportedError') {
          const recovered = await this.recoverVideoPlayback('play_not_supported')
          if (recovered) {
            this.pendingPlayAfterCanPlay = true
            return
          }
        }

        if (error && error.name !== 'AbortError') {
          this.videoLoadError = error.message || '视频暂时无法播放'
          if (this.isNetworkConnectionError(error)) {
            this.onPlaybackNetworkError(error)
          } else {
            this.$message.error('视频暂时无法播放，请稍后重试')
          }
        }
      }
    },

    // 更新观看次数
    async updateViewCount() {
      if (!this.videoId) {
        return
      }
      try {
        await updateVideoViewCount({ id: this.videoId })
      } catch (error) {
        console.error('更新观看次数失败:', error)
      }
    },

    // 播放/暂停切换
    async togglePlay() {
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      if (!video) return
      if (video.paused) {
        await this.safePlay()
      } else {
        if (player) {
          player.pause()
        } else {
          video.pause()
        }
      }
    },

    async togglePlayFromCenter() {
      if (!this.videoUrl || this.isVideoLoading || this.videoLoadError) {
        return
      }

      if (this.isMobilePage && this.isPlaying) {
        if (!this.showMobileControls) {
          this.showMobileControlsTemporarily()
          return
        }

        this.showMobileControlsTemporarily()
        return
      }

      await this.togglePlay()
      if (this.isMobilePage && this.isPlaying) {
        this.showMobileControlsTemporarily()
      }
    },

    // 处理播放
    handlePlay() {
      this.isPlaying = true
      if (this.materialMode) {
        this.startMaterialPlayTimer()
        this.startMaterialProgressTimer()
      }
      if (this.isMobilePage) {
        this.showMobileControls = true
        this.startMobileControlsAutoHide()
      }

      // 第一次播放时初始化播放进度功能
      const video = this.getVideoElement()
      if (!this.isProgressLoaded && this.videoId && video) {
        // 确保 duration 已设置
        if (video.duration && !isNaN(video.duration)) {
          this.duration = video.duration
        }
        this.initVideoProgress(this.videoId, video)
      }

      // 第一次播放时更新观看次数
      if (this.currentTime === 0) {
        this.updateViewCount()
      }
    },

    // 处理暂停
    handlePause() {
      this.isPlaying = false
      this.isPlaybackWaiting = false
      if (!this.videoLoadError) {
        this.isVideoLoading = false
        this.videoLoadingText = '视频加载中...'
      }
      if (this.materialMode) {
        this.stopMaterialPlayTimer()
        this.stopMaterialProgressTimer()
        this.reportTrainingMaterialProgress(true)
      }
      if (this.isMobilePage) {
        this.showMobileControls = true
      }
      this.clearMobileControlsHideTimer()
    },

    // 处理时间更新
    handleTimeUpdate() {
      const video = this.getVideoElement()
      if (!video) return
      this.currentTime = video.currentTime
      this.progressPercent = video.duration ? (video.currentTime / video.duration) * 100 : 0
      this.updateBufferedProgress()
      if (this.materialMode && this.progressPercent >= 95) {
        this.reportTrainingMaterialProgress(true)
      }
      this.syncPlaybackProgress(video.currentTime, video.duration)
    },

    handleProgress() {
      this.updateBufferedProgress()
    },

    // 处理元数据加载
    handleLoadedMetadata() {
      const video = this.getVideoElement()
      if (!video) return
      this.duration = video.duration
      video.playbackRate = this.playbackRate
      video.volume = this.volume / 100
      this.isVideoLoading = false
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.videoLoadError = ''
      this.updateBufferedProgress()
      this.onPlaybackNetworkRecovered()

      // 初始化播放进度功能
      if (!this.isProgressLoaded && this.videoId) {
        this.initVideoProgress(this.videoId, video)
      } else {
      }
    },

    handleEnded() {
      this.isPlaying = false
      if (this.materialMode) {
        this.stopMaterialPlayTimer()
        this.stopMaterialProgressTimer()
        this.reportTrainingMaterialProgress(true)
      }
    },

    // 新增调试事件
    handleLoadStart() {
      this.isVideoLoading = true
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.videoLoadError = ''
      this.hasVideoFrameReady = false
      this.bufferedPercent = 0
    },

    handleLoadedData() {
      this.isVideoLoading = false
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.hasVideoFrameReady = true
      this.updateBufferedProgress()
      this.onPlaybackNetworkRecovered()
      // 备用：如果 loadedmetadata 没触发，这里也尝试初始化
      this.tryInitVideoProgress()
    },

    handleCanPlay() {
      this.isVideoLoading = false
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.videoLoadError = ''
      this.hasVideoFrameReady = true
      this.updateBufferedProgress()
      this.onPlaybackNetworkRecovered()
      // 备用：如果 loadedmetadata 没触发，这里也尝试初始化
      this.tryInitVideoProgress()
      if (this.pendingPlayAfterCanPlay) {
        this.pendingPlayAfterCanPlay = false
        this.safePlay({ allowRecover: false })
      }
    },

    handlePlaying() {
      this.isVideoLoading = false
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.hasVideoFrameReady = true
      this.updateBufferedProgress()
    },

    handleWaiting() {
      const video = this.getVideoElement()
      if (!video || video.paused || video.ended || this.videoLoadError) {
        return
      }
      this.isVideoLoading = true
      this.isPlaybackWaiting = true
      this.videoLoadingText = '视频加载中...'
    },

    handleStalled() {
      const video = this.getVideoElement()
      if (!video || video.paused || video.ended || this.videoLoadError) {
        return
      }
      this.isVideoLoading = true
      this.isPlaybackWaiting = true
      this.videoLoadingText = '视频加载中...'
    },

    // 尝试初始化视频进度（防止 loadedmetadata 事件未触发）
    async handleVideoError() {
      const video = this.getVideoElement()
      const mediaError = video && video.error ? video.error : null
      const mediaErrorCode = mediaError ? Number(mediaError.code) : 0
      const isLikelyNetworkError = !mediaError || mediaErrorCode === 2

      this.isPlaying = false
      this.isVideoLoading = false
      this.isPlaybackWaiting = false
      this.videoLoadingText = '视频加载中...'
      this.videoLoadError = mediaError && mediaError.message ? mediaError.message : '视频资源加载失败'

      console.error('视频资源加载失败:', {
        videoId: this.videoId,
        src: video ? video.currentSrc || this.videoUrl : this.videoUrl,
        mediaErrorCode: mediaError ? mediaError.code : null,
        networkState: video ? video.networkState : null,
        readyState: video ? video.readyState : null
      })

      const compatibilityFallback = await this.fallbackToCompatibilityPlayback('media_error')
      if (compatibilityFallback) {
        this.videoLoadError = ''
        this.$message.warning('已切换为兼容播放模式，当前视频可播放，但截图功能将不可用。')
        return
      }

      if (isLikelyNetworkError) {
        this.registerPlaybackNetworkFailure()
      }

      const recovered = await this.recoverVideoPlayback('media_error')
      if (!recovered && !isLikelyNetworkError) {
        this.$message.error('视频加载失败，请刷新后重试')
      }
    },

    async retryVideoLoad() {
      const nextUrl = this.videoUrl || this.getVideoUrl(this.videoDetail.videoUrl)
      if (!nextUrl) {
        this.$message.error('视频地址不存在')
        return
      }

      this.videoErrorRetryCount = 0
      this.resetPlaybackNetworkState()
      await this.setVideoSource(nextUrl)
    },

    tryInitVideoProgress() {
      const video = this.getVideoElement()
      if (!video) {
        return
      }

      if (!this.videoId) {
        return
      }

      if (this.isProgressLoaded) {
        return
      }

      if (video.duration && !isNaN(video.duration)) {
        this.duration = video.duration
      }

      this.initVideoProgress(this.videoId, video)
    },

    // 切换静音
    toggleMute() {
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      if (!video) return
      if (player) {
        player.muted(!player.muted())
        this.isMuted = player.muted()
      } else {
        video.muted = !video.muted
        this.isMuted = video.muted
      }
    },

    // 改变音量
    changeVolume() {
      this.applyVolume(this.volume)
    },

    // 改变播放速度
    changePlaybackRate(rate) {
      this.playbackRate = rate
      const player = this.getVideoJsPlayer()
      const video = this.getVideoElement()
      if (!video) return
      if (player) {
        player.playbackRate(rate)
      } else {
        video.playbackRate = rate
      }
    },

    // 截图
    captureScreenshot() {
      const video = this.getVideoElement()
      if (!video) return

      try {
        const frameDataUrl = this.captureVideoFrameDataUrl(video)
        const a = document.createElement('a')
        a.href = frameDataUrl
        a.download = `screenshot-${Date.now()}.png`
        a.click()
        this.$message.success('截图已保存')
      } catch (error) {
        this.handleFrameCaptureError(error, '截图')
      }
    },

    captureVideoFrameDataUrl(video) {
      if (!video) {
        throw new Error('视频未加载')
      }
      if (!video.videoWidth || !video.videoHeight) {
        throw new Error('视频画面尚未就绪')
      }

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('当前浏览器不支持截图')
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL('image/png')
    },

    handleFrameCaptureError(error, actionName) {
      console.error(`${actionName}失败:`, error)
      const message = String((error && error.message) || '')
      const isSecurityError = error && (error.name === 'SecurityError' || /tainted|cross-origin|insecure/i.test(message))

      if (isSecurityError) {
        if (this.disableCrossOriginMedia) {
          this.$message.error('当前视频已切换为兼容播放模式，可正常播放，但无法截图。')
        } else {
          this.$message.error('浏览器仍然拒绝跨域视频截图，请检查该视频文件的实际响应头是否包含 Access-Control-Allow-Origin。')
        }
        return
      }

      this.$message.error(message || `${actionName}失败，请稍后重试`)
    },

    handlePosterLoad() {
      this.posterLoadFailed = false
    },

    handlePosterError() {
      this.posterLoadFailed = true
    },

    getFullscreenElement() {
      return document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement ||
        null
    },

    async requestElementFullscreen(target) {
      if (!target) return false

      try {
        if (target.requestFullscreen) {
          await target.requestFullscreen()
          return true
        }
        if (target.webkitRequestFullscreen) {
          target.webkitRequestFullscreen()
          return true
        }
        if (target.msRequestFullscreen) {
          target.msRequestFullscreen()
          return true
        }
      } catch (error) {
        return false
      }

      return false
    },

    async exitAnyFullscreen(video) {
      try {
        if (this.getFullscreenElement()) {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
            return
          }
          if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen()
            return
          }
          if (document.msExitFullscreen) {
            document.msExitFullscreen()
            return
          }
        }

        if (video && video.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
          video.webkitExitFullscreen()
        }
      } catch (error) {
      }
    },

    syncNativeFullscreenState() {
      const video = this.getVideoElement()
      this.isNativeFullscreenActive = !!this.getFullscreenElement() || !!(video && video.webkitDisplayingFullscreen)
    },

    setDocumentScrollLock(locked) {
      const overflow = locked ? 'hidden' : ''
      document.body.style.overflow = overflow
      document.documentElement.style.overflow = overflow
    },

    enterMobileCssFullscreen() {
      if (!this.isMobilePage || this.isMobileCssFullscreen) return
      this.isMobileCssFullscreen = true
      this.showMobileControls = true
      this.setDocumentScrollLock(true)
      this.$nextTick(() => {
        this.showMobileControlsTemporarily()
      })
    },

    exitMobileCssFullscreen() {
      if (!this.isMobileCssFullscreen) {
        this.setDocumentScrollLock(false)
        return
      }

      this.isMobileCssFullscreen = false
      this.showMobileControls = true
      this.stopMobileSeekGesture()
      this.setDocumentScrollLock(false)
    },

    async lockLandscapeOrientation() {
      if (!this.isMobilePage) return
      if (!window.screen || !screen.orientation || !screen.orientation.lock) return

      try {
        await screen.orientation.lock('landscape')
        this.isOrientationLocked = true
      } catch (error) {
        // 部分移动浏览器会拒绝方向锁定，静默降级
      }
    },

    async unlockOrientationIfNeeded() {
      if (!this.isOrientationLocked) return
      if (!window.screen || !screen.orientation || !screen.orientation.unlock) return

      try {
        screen.orientation.unlock()
      } catch (error) {
      } finally {
        this.isOrientationLocked = false
      }
    },

    handleFullscreenChange() {
      this.syncNativeFullscreenState()
      if (!this.isNativeFullscreenActive && !this.isMobileCssFullscreen) {
        this.stopMobileSeekGesture()
        this.unlockOrientationIfNeeded()
      }
    },

    handleWebkitBeginFullscreen() {
      this.isNativeFullscreenActive = true
      this.lockLandscapeOrientation()
    },

    handleWebkitEndFullscreen() {
      this.isNativeFullscreenActive = false
      this.stopMobileSeekGesture()
      if (!this.isMobileCssFullscreen) {
        this.unlockOrientationIfNeeded()
      }
    },

    // 切换全屏
    async toggleFullscreen() {
      const video = this.getVideoElement()
      const wrapper = this.$refs.videoWrapper || (video && video.parentElement)
      if (!video || !wrapper) return

      if (this.isMobileCssFullscreen) {
        this.exitMobileCssFullscreen()
        await this.unlockOrientationIfNeeded()
        return
      }

      const inNativeFullscreen = this.isNativeFullscreenActive || !!this.getFullscreenElement() || !!video.webkitDisplayingFullscreen
      if (inNativeFullscreen) {
        await this.exitAnyFullscreen(video)
        return
      }

      let entered = await this.requestElementFullscreen(wrapper)
      if (!entered && this.isMobilePage) {
        this.enterMobileCssFullscreen()
        entered = true
      } else if (!entered) {
        entered = await this.requestElementFullscreen(video)

        // 仅桌面端保留视频原生全屏回退，避免移动端丢失自定义控件与手势层
        if (!entered && video.webkitEnterFullscreen) {
          try {
            video.webkitEnterFullscreen()
            entered = true
          } catch (error) {
            entered = false
          }
        }
      }

      if (entered) {
        this.syncNativeFullscreenState()
        if (this.isMobilePage) {
          this.showMobileControlsTemporarily()
        }
        await this.lockLandscapeOrientation()
      }
    },

    // 格式化时间
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      return dateString.split(' ')[0]
    },

    formatCount(count) {
      const num = Number(count) || 0
      return num.toLocaleString('zh-CN')
    },

    // 加载相关推荐视频
    async loadRelatedVideos() {
      if (!this.videoId || !this.videoDetail.categoryName) {
        this.relatedVideos = []
        return
      }
      try {
        const res = await getRelatedVideoList({
          categoryName: this.videoDetail.categoryName,
          currentVideoId: this.videoId
        })

        if (res.code === 1000 && res.data) {
          this.relatedVideos = res.data.map(video => ({
            id: video.videoId,
            title: video.videoTitle,
            thumbnail: this.getVideoUrl(video.videoCover),
            duration: this.formatDuration(video.duration)
          }))
        }
      } catch (error) {
        console.error('加载相关推荐视频失败:', error)
      }
    },

    // 加载右侧最新视频（按来源区分：公区/部门/集中活动）
    async loadCategoryLatestVideos() {
      if (!this.videoId) {
        this.categoryLatestVideos = []
        return
      }

      this.isLoadingCategoryLatest = true
      try {
        let latestItems = []
        const currentVideoId = String(this.videoId)

        // 培训资料页不展示右侧最新列表
        if (this.fromTraining) {
          this.categoryLatestVideos = []
          return
        }

        // 集中活动：使用活动分页接口，按创建时间倒序
        if (!this.fromPublic && !this.fromDepartment) {
          const activityRes = await getVideoActivityPage({
            pageNumber: 1,
            pageSize: 12,
            sortField: 'create_time',
            sortOrder: 'desc'
          })

          if (activityRes.code === 1000 && activityRes.data) {
            const activityRecords = activityRes.data.records || []
            latestItems = activityRecords.map(activity => {
              const video = activity.videoManage || {}
              return {
                id: activity.videoId || video.videoId,
                title: activity.activityName || activity.videoName || activity.videoTitle || video.videoTitle || '未命名活动',
                thumbnail: this.getVideoUrl(video.videoCover || activity.videoCover || ''),
                duration: this.formatDuration(video.duration != null ? video.duration : activity.duration),
                viewCount: Number(video.viewCount != null ? video.viewCount : (activity.viewCount || 0)),
                createTime: video.createTime || activity.createTime || '',
                watchStatus: video.watchStatus || activity.watchStatus || '待观看'
              }
            })
          }
        } else {
          // 公区/部门：使用视频管理分页接口，按创建时间倒序
          let deptId = ''
          if (this.fromDepartment) {
            deptId = this.currentUser && this.currentUser.deptId ? this.currentUser.deptId : ''
            if (!deptId) {
              try {
                const userRes = await getUserInfo()
                if (userRes.code === 1000 && userRes.data) {
                  deptId = userRes.data.deptId || ''
                }
              } catch (error) {
                deptId = ''
              }
            }
          }

          const videoRes = await getVideoManagePage({
            pageNumber: 1,
            pageSize: 12,
            sortField: 'create_time',
            sortOrder: 'desc',
            visibility: this.fromDepartment ? 1 : 0,
            deptId
          })

          if (videoRes.code === 1000 && videoRes.data) {
            const records = videoRes.data.records || []
            latestItems = records.map(video => ({
              id: video.videoId,
              title: video.videoTitle || '未命名视频',
              thumbnail: this.getVideoUrl(video.videoCover || ''),
              duration: this.formatDuration(video.duration),
              viewCount: Number(video.viewCount || 0),
              createTime: video.createTime || '',
              watchStatus: video.watchStatus || '待观看'
            }))
          }
        }

        // 兜底保证按时间由新到旧，并过滤当前视频
        this.categoryLatestVideos = latestItems
          .filter(video => String(video.id || '') !== currentVideoId)
          .sort((a, b) => {
            const aTime = a.createTime ? new Date(a.createTime).getTime() : 0
            const bTime = b.createTime ? new Date(b.createTime).getTime() : 0
            return bTime - aTime
          })
          .slice(0, 10)
      } catch (error) {
        console.error('加载右侧最新视频失败:', error)
        this.categoryLatestVideos = []
      } finally {
        this.isLoadingCategoryLatest = false
      }
    },

    // 格式化视频时长（秒转为 mm:ss 格式）
    formatDuration(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    // 点击相关推荐视频跳转
    handleRelatedVideoClick(videoId) {
      const query = { id: videoId }
      if (this.fromPublic) {
        query.from = 'public'
      } else if (this.fromDepartment) {
        query.from = 'department'
      } else if (this.fromTraining) {
        query.from = 'training'
      } else {
        query.from = 'activity'
      }

      this.$router.push({
        path: '/videoPlay',
        query
      })
    },

    // 加载笔记列表
    async loadNoteList(isLoadMore = false) {
      if (!this.videoId) {
        return
      }
      if (this.isLoadingNotes || (!isLoadMore && this.pageNumber > 1)) {
        return
      }

      try {
        this.isLoadingNotes = true

        const params = {
          taskId: this.videoId,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        }

        const res = await getApeArticlePage(params)

        if (res.code === 1000 && res.data) {
          const newNotes = res.data.records || res.data || []

          if (isLoadMore) {
            // 加载更多时，追加数据
            this.mockNotes = [...this.mockNotes, ...newNotes]
          } else {
            // 初次加载时，替换数据
            this.mockNotes = newNotes
          }

          // 判断是否还有更多数据
          if (res.data.records) {
            // 如果返回的数据少于pageSize，说明没有更多了
            this.hasMore = newNotes.length >= this.pageSize
          } else {
            this.hasMore = false
          }
        }
      } catch (error) {
        console.error('加载笔记列表失败:', error)
        this.$message.error('加载笔记失败，请稍后重试')
      } finally {
        this.isLoadingNotes = false
      }
    },

    // 加载更多笔记
    async loadMoreNotes() {
      if (!this.hasMore || this.isLoadingNotes) {
        return
      }

      this.pageNumber++
      await this.loadNoteList(true)
    },

    // 切换笔记折叠状态
    toggleNotesExpanded() {
      this.notesExpanded = !this.notesExpanded
    },

    // 获取笔记头像URL
    getNoteAvatar(avatar) {
      if (!avatar) return DEFAULT_AVATAR_PLACEHOLDER
      if (avatar.startsWith('http')) return avatar
      return this.$store.state.HOST + avatar
    },

    // 获取作者头像URL
    getAuthorAvatar(avatar) {
      if (!avatar) return DEFAULT_AVATAR_PLACEHOLDER
      if (avatar.startsWith('http')) return avatar
      return this.$store.state.HOST + avatar
    },

    // 获取笔记缩略图URL
    getNoteThumbnail(thumbnail) {
      if (!thumbnail) return ''
      if (thumbnail.startsWith('http')) return thumbnail
      return this.$store.state.HOST + thumbnail
    },

    // 预览图片
    previewImage(thumbnail) {
      this.previewImageUrl = this.getNoteThumbnail(thumbnail)
      this.showImagePreview = true
    },

    // 关闭图片预览
    closeImagePreview() {
      this.showImagePreview = false
      this.previewImageUrl = ''
    },

    // 打开创建笔记弹框
    openCreateNoteDialog() {
      const video = this.getVideoElement()
      if (!video) {
        this.$message.error('视频未加载')
        return
      }

      // 获取当前播放时间
      this.noteTimestamp = this.formatTime(video.currentTime)

      try {
        this.screenshotDataUrl = this.captureVideoFrameDataUrl(video)
      } catch (error) {
        this.handleFrameCaptureError(error, '生成截图笔记')
        return
      }

      // 清空之前的笔记内容
      this.noteContent = ''

      // 显示弹框
      this.showNoteDialog = true
    },

    // 关闭笔记弹框
    closeNoteDialog() {
      this.showNoteDialog = false
      this.noteContent = ''
      this.screenshotDataUrl = ''
      this.noteTimestamp = ''
    },

    // 保存笔记
    async saveNote() {
      if (!this.noteContent.trim()) {
        this.$message.warning('请输入笔记内容')
        return
      }

      // 显示加载提示
      const loading = this.$loading({
        lock: true,
        text: '正在保存笔记...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        // 1. 将 base64 转换为 Blob 文件
        const base64Data = this.screenshotDataUrl.split(',')[1]
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'image/png' })
        const file = new File([blob], `note-${Date.now()}.png`, { type: 'image/png' })

        // 2. 上传图片到服务器
        const uploadRes = await uploadFile(file)

        if (uploadRes.code !== 1000 || !uploadRes.message) {
          loading.close()
          this.$message.error('图片上传失败')
          return
        }

        const thumbnailPath = uploadRes.message

        // 3. 调用保存笔记接口
        const noteData = {
          taskId: this.videoId,
          title: this.videoDetail.videoTitle,
          content: this.noteContent,
          timestamp: this.noteTimestamp,
          thumbnail: thumbnailPath
        }

        const saveRes = await insertApeArticle(noteData)

        loading.close()

        if (saveRes.code === 1000) {
          // 显示成功消息
          this.$message.success('笔记创建成功')

          // 重置分页并重新加载笔记列表
          this.pageNumber = 1
          this.hasMore = true
          this.mockNotes = []
          await this.loadNoteList()

          // 关闭弹框
          this.closeNoteDialog()
        } else {
          this.$message.error(saveRes.message || '笔记保存失败')
        }
      } catch (error) {
        loading.close()
        console.error('保存笔记失败:', error)
        this.$message.error('笔记保存失败，请稍后重试')
      }
    }
  },
  mounted() {
    // 重置窗口滚动位置到顶部
    window.scrollTo(0, 0)
    this.handleViewportChange()
    window.addEventListener('resize', this.handleViewportChange)
    window.addEventListener('keydown', this.handleGlobalKeydown)
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('msfullscreenchange', this.handleFullscreenChange)
    this.syncNativeFullscreenState()

    if (this.initRouteContext()) {
      this.$nextTick(async () => {
        const isBlocked = await this.validateTrainingAccessInPlan()
        if (!isBlocked) {
          await this.initializeTrainingMaterialPlayback()
        }
      })
      return
    }

    this.videoId = this.$route.query.id
    if (!this.videoId) {
      this.$message.error('视频ID不存在')
      this.$router.push('/')
      return
    }
    this.$nextTick(() => {
      this.loadVideoDetail()
      this.loadNoteList()
    })
  },
  beforeDestroy() {
    this.reportTrainingMaterialProgress(true)
    this.resetMaterialProgressState()
    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('keydown', this.handleGlobalKeydown)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('msfullscreenchange', this.handleFullscreenChange)
    this.cleanupProgressDrag()
    this.clearMobileControlsHideTimer()
    this.stopMobileSeekGesture()
    this.exitMobileCssFullscreen()
    this.unlockOrientationIfNeeded()
    this.disposeVideoJsPlayer()
  },
  watch: {
    // 监听路由变化，实现点击推荐视频后刷新页面数据
    '$route.fullPath'() {
      window.scrollTo(0, 0)
      this.reportTrainingMaterialProgress(true)
      this.resetMaterialProgressState()
      this.stopMobileSeekGesture()
      this.exitMobileCssFullscreen()

      if (this.initRouteContext()) {
        if (typeof this.cleanupVideoProgress === 'function') {
          this.cleanupVideoProgress()
        }
        if (typeof this.resetVideoProgressState === 'function') {
          this.resetVideoProgressState()
        }
        this.$nextTick(async () => {
          const isBlocked = await this.validateTrainingAccessInPlan()
          if (!isBlocked) {
            await this.initializeTrainingMaterialPlayback()
          }
        })
        return
      }

      const newId = this.$route.query.id
      if (!newId || newId === this.videoId) {
        return
      }

      if (typeof this.cleanupVideoProgress === 'function') {
        this.cleanupVideoProgress()
      }
      if (typeof this.resetVideoProgressState === 'function') {
        this.resetVideoProgressState()
      }

      this.videoId = newId
      this.resetVideoPlaybackState()
      this.videoUrl = ''
      this.videoDetail = {}
      this.pageNumber = 1
      this.hasMore = true
      this.mockNotes = []
      this.notesExpanded = false
      this.categoryLatestVideos = []

      this.loadVideoDetail()
      this.loadNoteList()
    }
  }
}
</script>

<style scoped>
.video-play-page {
  font-family: 'Manrope', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #101318;
  background-color: #f8fafc;
  min-height: 100vh;
}

.main-content {
  width: 100%;
  max-width: 1600px;
  margin: 10px auto 0;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-height: calc(100vh - 64px);
}

.container {
  width: 100%;
}

.content-wrapper {
  display: flex;
  gap: 2rem;
}

.video-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.video-network-alert {
  margin-bottom: -0.5rem;
}

/* 视频播放器 */
.video-player-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #000;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid #1f2937;
}

.video-player-wrapper:fullscreen,
.video-player-wrapper:-webkit-full-screen,
.video-player-wrapper.is-mobile-fullscreen {
  width: 100vw;
  max-width: none;
  height: 100vh;
  height: 100dvh;
  aspect-ratio: auto;
  border-radius: 0;
}

.video-player-wrapper.is-mobile-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 3000;
}

.video-player-wrapper:hover .video-controls {
  opacity: 1;
}

.video-player-wrapper.is-mobile .video-controls {
  opacity: 1;
}

.video-player {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-poster-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: #000;
}

.video-poster-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.video-status-overlay {
  position: absolute;
  inset: 0;
  z-index: 14;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: rgba(0, 0, 0, 0.38);
  color: #fff;
  backdrop-filter: blur(2px);
}

.video-status-overlay.is-error {
  background: rgba(0, 0, 0, 0.56);
}

.video-status-icon {
  font-size: 28px;
}

.video-status-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.video-retry-btn {
  border: none;
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  background: #1a71ff;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease;
}

.video-retry-btn:hover {
  background: #0056e0;
}

.continue-play-tip {
  position: absolute;
  left: 1rem;
  bottom: 4.5rem;
  z-index: 15;
  max-width: min(440px, calc(100% - 2rem));
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.875rem;
  background: rgba(15, 23, 42, 0.72);
  color: #f8fafc;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(10px);
}

.continue-play-tip.is-mobile {
  left: 0.75rem;
  right: 0.75rem;
  bottom: 4.75rem;
  max-width: none;
  padding: 0.625rem 0.75rem;
  gap: 0.5rem;
}

.continue-play-tip-text {
  font-size: 13px;
  line-height: 1.4;
  font-weight: 600;
}

.continue-play-tip-action {
  border: none;
  background: transparent;
  color: #4da3ff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  padding: 0;
}

.continue-play-tip-action:hover {
  color: #7dc1ff;
}

.continue-play-tip-fade-enter-active,
.continue-play-tip-fade-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.continue-play-tip-fade-enter,
.continue-play-tip-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.center-play-toggle-zone {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 10;
  width: min(42vw, 320px);
  height: min(42vh, 220px);
  transform: translate(-50%, -50%);
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  appearance: none;
}

.center-play-toggle-zone:focus,
.center-play-toggle-zone:focus-visible,
.center-play-toggle-zone:active {
  outline: none;
  box-shadow: none;
}

.center-play-toggle-zone.is-playing {
  cursor: pointer;
}

.mobile-seek-gesture-layer {
  position: absolute;
  inset: 0;
  z-index: 11;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}

.mobile-seek-zone {
  width: 34%;
  height: 100%;
  border: none;
  padding: 0;
  background: transparent;
  color: #fff;
  pointer-events: auto;
  cursor: pointer;
  transition: background 0.18s ease;
}

.mobile-seek-zone.is-active {
  background: rgba(15, 23, 42, 0.18);
}

.mobile-seek-zone.is-left.is-active {
  background: linear-gradient(to right, rgba(15, 23, 42, 0.4), rgba(15, 23, 42, 0.08), transparent);
}

.mobile-seek-zone.is-right.is-active {
  background: linear-gradient(to left, rgba(15, 23, 42, 0.4), rgba(15, 23, 42, 0.08), transparent);
}

.mobile-seek-indicator {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 13;
  transform: translate(-50%, -50%);
  padding: 0.625rem 0.875rem;
  border-radius: 9999px;
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  backdrop-filter: blur(8px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.28);
  pointer-events: none;
}

.video-overlay {
  position: absolute;
  inset: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s;
  pointer-events: auto;
}

.video-overlay:hover {
  background: rgba(0, 0, 0, 0.2);
}

.play-button {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s;
}

.play-button i {
  font-size: 40px;
}

.play-button:hover {
  opacity: 1;
}

.video-overlay:hover .play-button {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* 控制栏 */
.video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 16;
  padding: 1rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
  color: #fff;
  opacity: 0;
  transition: opacity 0.3s;
}

.video-player-wrapper:fullscreen .video-controls,
.video-player-wrapper:-webkit-full-screen .video-controls,
.video-player-wrapper.is-mobile-fullscreen .video-controls {
  padding-top: calc(1rem + env(safe-area-inset-top));
  padding-right: calc(1rem + env(safe-area-inset-right));
  padding-bottom: calc(1rem + env(safe-area-inset-bottom));
  padding-left: calc(1rem + env(safe-area-inset-left));
}

.video-player-wrapper:hover .video-controls {
  opacity: 1;
}

.progress-bar-wrapper {
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 9999px;
  margin-bottom: 1rem;
  cursor: pointer;
  overflow: hidden;
  pointer-events: auto;
  touch-action: none;
}

.progress-bar {
  width: 100%;
  height: 100%;
  position: relative;
}

.progress-filled {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #1a71ff;
  border-radius: 9999px;
  transition: width 0.1s;
  z-index: 2;
}

.progress-buffered {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 9999px;
  transition: width 0.2s;
  z-index: 1;
}

.controls-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
}

.controls-left,
.controls-right {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.control-btn {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-btn i {
  font-size: 20px;
}

.control-btn:hover {
  color: #1a71ff;
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.volume-slider {
  width: 64px;
}

.volume-input {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 9999px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}

.volume-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
}

.volume-input::-moz-range-thumb {
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

.time-display {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
  font-variant-numeric: tabular-nums;
}

.playback-rate {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
}

.rate-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.rate-btn:hover {
  color: #1a71ff;
}

.rate-btn.active {
  color: #1a71ff;
  text-decoration: underline;
}

.rate-select {
  height: 24px;
  min-width: 52px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 0 4px;
  outline: none;
}

/* 视频信息 */
.video-info-section {
  margin-top: 0;
}

.video-title {
  font-size: 1.875rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.25;
  margin: 0 0 1rem 0;
}

.video-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.875rem 1rem;
  padding: 0.75rem 0;
  border-top: 1px solid #eef2f7;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
}

.video-meta-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.875rem;
  min-width: 0;
}

.meta-author {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.author-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  background-size: cover;
  background-position: center;
  border: 1px solid #ffffff;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.16);
}

.author-name {
  font-weight: 700;
  color: #111827;
}

.meta-v-separator {
  width: 1px;
  height: 16px;
  background: #e5e7eb;
}

.meta-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #6b7280;
}

.meta-icon {
  font-size: 16px;
  color: #6b7280;
}

.meta-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  padding: 0.25rem 0.75rem;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.video-meta-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 500;
  margin-left: auto;
  white-space: nowrap;
}

.meta-stat-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.stat-icon {
  font-size: 14px;
  color: #9ca3af;
}

.meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #d1d5db;
}

/* AI摘要 */
.ai-summary-section {
  margin-top: 0;
}

.summary-details {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.summary-header:hover {
  background: #f9fafb;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
  font-weight: 700;
}


.expand-icon {
  color: #9ca3af;
  font-size: 20px;
  transition: transform 0.3s;
}

.summary-details[open] .expand-icon {
  transform: rotate(180deg);
}

.summary-content {
  padding: 0 1.5rem 1.5rem;
}

.summary-markdown {
  position: relative;
  z-index: 0;
  background: rgba(26, 113, 255, 0.05);
  border-left: 4px solid #1a71ff;
  border-radius: 0.5rem;
  overflow: hidden;
}

::v-deep .summary-markdown .v-note-wrapper {
  border: none !important;
  min-height: auto !important;
  box-shadow: none !important;
  position: relative !important;
  z-index: 0 !important;
}

::v-deep .summary-markdown .v-show-content {
  padding: 1rem !important;
  line-height: 1.8;
  color: #374151;
  font-size: 14px;
  background: transparent !important;
  position: relative !important;
  z-index: 0 !important;
}

::v-deep .summary-markdown .markdown-body {
  position: relative !important;
  z-index: 0 !important;
}

/* 相关推荐 */
.related-section {
  margin-top: 0;
  padding-top: 1rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #111827;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.related-card {
  cursor: pointer;
  transition: all 0.3s;
}

.related-card:hover .related-title {
  color: #1a71ff;
}

.related-thumbnail {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.related-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.related-card:hover .related-thumbnail img {
  transform: scale(1.05);
}

.video-duration {
  position: absolute;
  bottom: 0.25rem;
  right: 0.25rem;
  padding: 0 0.25rem;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 10px;
  border-radius: 0.125rem;
}

.related-title {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  color: #374151;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.2s;
}

/* 笔记（内联） */
.notes-inline-section {
  margin-top: 0;
  padding: 1rem 1.25rem 0.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
}

.notes-inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.notes-inline-list {
  max-height: none;
}

.notes-inline-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0 1rem;
}

.notes-toggle-btn,
.notes-load-more-btn {
  border: none;
  border-radius: 999px;
  padding: 0.375rem 0.875rem;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notes-toggle-btn {
  background: #eff6ff;
  color: #1d4ed8;
}

.notes-toggle-btn:hover {
  background: #dbeafe;
}

.notes-load-more-btn {
  background: #1a71ff;
  color: #fff;
}

.notes-load-more-btn:hover {
  background: #0056e0;
}

.notes-load-more-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* 右侧同分类最新 */
.latest-panel {
  width: 380px;
  flex-shrink: 0;
}

.latest-container {
  position: sticky;
  top: 1.5rem;
  max-height: calc(100vh - 3rem);
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.latest-list {
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.latest-list::-webkit-scrollbar {
  width: 4px;
}

.latest-list::-webkit-scrollbar-track {
  background: transparent;
}

.latest-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 20px;
}

.latest-item {
  display: flex;
  gap: 0.625rem;
  padding: 0.5rem;
  border-radius: 0.625rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.latest-item:hover {
  background: #f8fafc;
}

.latest-thumbnail {
  position: relative;
  width: 160px;
  min-width: 160px;
  aspect-ratio: 16 / 9;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #e5e7eb;
}

.latest-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.latest-watch-status {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  pointer-events: none;
  transform: scale(0.39);
  transform-origin: top right;
}

.latest-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.latest-item-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
  color: #111827;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.latest-item-meta {
  margin-top: 0.5rem;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.latest-empty {
  padding: 1rem 0.5rem 1.25rem;
  text-align: center;
}

.create-note-btn {
  background: #1a71ff;
  color: #fff;
  padding: 0.375rem 1rem;
  border-radius: 0.5rem;
  font-size: 14px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.create-note-btn:hover {
  background: #0056e0;
}

.note-item {
  margin-bottom: 1.5rem;
}

.note-header-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.note-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 1px solid #f3f4f6;
}

.note-meta {
  display: flex;
  flex-direction: column;
}

.note-author {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.note-timestamp {
  font-size: 11px;
  color: #1a71ff;
  background: rgba(26, 113, 255, 0.05);
  padding: 0 0.25rem;
  border-radius: 0.125rem;
  font-family: 'Courier New', monospace;
}

.note-content {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  margin: 0 0 0.75rem 0.25rem;
}

.note-screenshot {
  position: relative;
  width: 160px;
  aspect-ratio: 16 / 9;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  cursor: zoom-in;
  margin-left: 0.25rem;
}

.note-screenshot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.screenshot-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.note-screenshot:hover .screenshot-overlay {
  opacity: 1;
}


.note-divider {
  border: 0;
  border-top: 1px solid #f3f4f6;
  margin: 1.5rem 0;
}

/* 加载状态样式 */
.notes-loading {
  text-align: center;
  padding: 1rem;
}

.loading-text {
  font-size: 12px;
  color: #9ca3af;
}

/* 没有更多数据提示 */
.notes-no-more {
  text-align: center;
  padding: 1rem;
}

.no-more-text {
  font-size: 12px;
  color: #9ca3af;
}

/* 空状态样式 */
.notes-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 0.75rem;
}

.empty-icon {
  font-size: 48px;
  color: #d1d5db;
}

.empty-text {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

/* 图片预览弹框 */
.image-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  cursor: zoom-out;
}

.image-preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.image-preview-container img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 0.5rem;
}

.image-preview-close {
  position: absolute;
  top: -3rem;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.image-preview-close:hover {
  background: rgba(255, 255, 255, 0.2);
}


/* 创建笔记弹框样式 */
.note-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  overflow-y: auto;
  padding: 2rem 1rem;
}

.note-dialog {
  background: #ffffff;
  width: 100%;
  max-width: 42rem;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: auto;
}

/* 弹框头部 */
.note-dialog-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(248, 250, 252, 0.5);
}

.note-dialog-user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.note-dialog-avatar {
  height: 2.5rem;
  width: 2.5rem;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex-shrink: 0;
}

.note-dialog-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.note-dialog-username {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.note-dialog-video-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
}


.note-dialog-close {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.note-dialog-close:hover {
  color: #64748b;
}


/* 弹框内容 */
.note-dialog-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.note-dialog-content::-webkit-scrollbar {
  width: 6px;
}

.note-dialog-content::-webkit-scrollbar-track {
  background: transparent;
}

.note-dialog-content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 20px;
}

/* 时间点标记 */
.note-time-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: rgba(26, 113, 255, 0.05);
  color: #1a71ff;
  border-radius: 0.5rem;
  width: fit-content;
}


.note-time-text {
  font-size: 0.875rem;
  font-weight: 700;
}

/* 笔记内容区域 */
.note-content-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.note-section-title {
  color: #0f172a;
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
}

.note-textarea-wrapper {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.note-textarea {
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.875rem;
  color: #334155;
  line-height: 1.6;
  resize: vertical;
  font-family: 'Manrope', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.note-textarea::placeholder {
  color: #94a3b8;
}

/* 视频截图区域 */
.note-screenshot-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.note-screenshot-preview {
  position: relative;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.note-screenshot-preview img {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
}

.screenshot-overlay-layer {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.1);
  transition: background 0.3s;
}

.note-screenshot-preview:hover .screenshot-overlay-layer {
  background: transparent;
}

.screenshot-time-badge {
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.625rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}


/* 弹框底部 */
.note-dialog-footer {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

.note-btn-cancel {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 0.375rem;
}

.note-btn-cancel:hover {
  background: #e2e8f0;
}

.note-btn-save {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  background: #1a71ff;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

/* 面包屑 */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 24px;
}

.breadcrumb-link {
  color: #6b7280;
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover {
  color: #1a71ff;
}

.breadcrumb-separator {
  color: #9ca3af;
  font-size: 16px;
}

.breadcrumb-current {
  color: #101318;
  font-weight: 600;
}

.note-btn-save:hover {
  background: #0056e0;
}


/* 响应式设计 */
@media (max-width: 1024px) {
  .content-wrapper {
    flex-direction: column;
  }

  .latest-panel {
    width: 100%;
  }

  .latest-container {
    position: static;
    max-height: none;
  }

  .latest-thumbnail {
    width: 180px;
    min-width: 180px;
  }

  .notes-inline-header {
    flex-wrap: wrap;
  }

  .notes-inline-actions {
    flex-wrap: wrap;
  }

  .latest-list {
    max-height: 600px;
  }

  .related-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .center-play-toggle-zone {
    width: min(58vw, 240px);
    height: min(38vh, 180px);
  }

  .related-grid {
    grid-template-columns: 1fr;
  }

  .latest-item {
    flex-direction: column;
  }

  .latest-thumbnail {
    width: 100%;
    min-width: 100%;
  }

  .video-title {
    font-size: 1.5rem;
  }

  .video-meta {
    align-items: flex-start;
  }

  .video-meta-right {
    margin-left: 0;
    width: 100%;
    white-space: normal;
  }

  .note-dialog {
    max-width: 100%;
    margin: 0;
  }

  .note-dialog-content {
    max-height: 60vh;
  }
}
</style>
